import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { useSanityPage } from '@/lib/sanity.hooks';
import {
  Users,
  FileText,
  Calendar,
  CheckSquare,
  Target,
  DollarSign,
  Zap,
  ArrowRight,
  Check,
  TrendingUp,
} from 'lucide-react';
import {
  SectionBadge,
  GlowOrbBackground,
  SectionDivider,
  AnimatedCounter,
} from '@/components/marketing';

export const ContractorsPage = () => {
  const { data: cmsData } = useSanityPage('contractors');

  const pageTitle = cmsData?.seo?.metaTitle || 'Roofing Contractor Software — Manage Every Job';
  const pageDescription =
    cmsData?.seo?.metaDescription ||
    cmsData?.description ||
    'Rooflyzer OS helps roofing contractors manage leads to completion. Track jobs, crews, materials, and payments in one platform.';
  const heroTitle = cmsData?.title || 'Run Your Roofing Business Without the Chaos.';
  const heroSubtitle =
    cmsData?.description ||
    'Shed the spreadsheet. Rooflyzer OS gives you a simple, powerful command center for managing every job from "New Lead" to "Paid in Full." One platform that connects your entire business — from marketing attribution to project completion.';

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl="/contractors"
        keywords="roofing contractor software, roofing job management, roofing crew management"
        ogImage={cmsData?.seo?.ogImage?.asset?.url || '/social-card.svg'}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rooflyzer.pro' },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'For Contractors',
              item: 'https://rooflyzer.pro/contractors',
            },
          ],
        }}
      />

      {/* ── Hero ──────────────────────────────────────────────────── */}
      <section className="relative pt-28 pb-16 px-4 sm:px-6 text-center overflow-hidden">
        <GlowOrbBackground
          orbs={[
            { color: 'purple', size: 650, top: '-200px', left: '-150px', opacity: 0.16 },
            { color: 'gold', size: 450, top: '100px', right: '-100px', opacity: 0.12 },
          ]}
        />

        <div className="relative z-10 max-w-4xl mx-auto">
          <SectionBadge icon={Users} text="For Roofing Contractors" color="gold" className="mb-6" />

          <h1 className="hero-h1 font-bold text-white mb-5">
            {cmsData?.title ? (
              heroTitle
            ) : (
              <>
                Run Your Roofing Business <br />{' '}
                <span className="text-gradient-gold-purple">Without the Chaos.</span>
              </>
            )}
          </h1>

          <p className="hero-lead text-white/65 max-w-2xl mx-auto mb-8">{heroSubtitle}</p>

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              size="lg"
              onClick={() => window.location.assign('/demo')}
              className="w-full sm:w-auto bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 shadow-sm"
            >
              See the Dashboard
              <ArrowRight className="ml-2 size-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => (window.location.href = '/contact')}
              className="w-full sm:w-auto border border-slate-700 text-slate-200 hover:text-white hover:bg-slate-800/60 px-8"
            >
              Schedule Demo Call
            </Button>
          </div>
        </div>
      </section>

      <SectionDivider className="max-w-5xl mx-auto px-4" />

      {/* ── Dashboard Preview ─────────────────────────────────────── */}
      <section className="py-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden glass-panel-elevated">
            <div className="absolute inset-0 rounded-3xl border border-white/[0.06] z-20 pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent z-10" />
            <img
              src="/product-frames/dashboard.webp"
              alt="Rooflyzer OS — Integrated contractor pipeline dashboard and operational controls"
              className="w-full h-auto"
              loading="lazy"
            />
            <div className="absolute bottom-6 left-6 z-20 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              <span className="px-3 py-1 rounded-full text-xs font-mono bg-slate-900/90 border border-slate-700 text-slate-200">
                Contractor Command Console
              </span>
            </div>
          </div>
        </div>
      </section>

      <SectionDivider className="max-w-5xl mx-auto px-4" />

      {/* ── Key Benefits ──────────────────────────────────────────── */}
      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6">
          {[
            {
              icon: Target,
              title: 'Complete Attribution',
              description:
                'Know exactly which marketing channels drive revenue. Increase ROI by 200-300% and redirect your budget effectively.',
              color: '#38BDF8',
            },
            {
              icon: Zap,
              title: 'Automated Workflows',
              description:
                'Never miss a customer follow-up. Automated lead nurturing increases overall close rates by 20-30%.',
              color: '#10B981',
            },
            {
              icon: DollarSign,
              title: 'Get Paid Faster',
              description:
                'Professional invoices and automated reminders reduce payment delays by 20-30% and eliminate collections stress.',
              color: '#6366F1',
            },
          ].map((benefit, index) => (
            <div
              key={index}
              className="glass-panel-interactive rounded-3xl p-8 reveal-on-scroll delay-1 border border-slate-800 bg-slate-900/40"
            >
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                style={{
                  background: `${benefit.color}18`,
                  border: `1px solid ${benefit.color}30`,
                }}
              >
                <benefit.icon className="w-7 h-7" style={{ color: benefit.color }} />
              </div>
              <h3 className="section-h3 font-bold text-white mb-4">{benefit.title}</h3>
              <p className="body-lg text-white/60 leading-relaxed">{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      <SectionDivider className="max-w-5xl mx-auto px-4" />

      {/* ── Features Grid ─────────────────────────────────────────── */}
      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto space-y-12">
          {[
            {
              icon: Users,
              title: 'Lead Pipeline & Management',
              description:
                'Visualize your sales funnel with a drag-and-drop Kanban board. Move deals between stages from first click to signed contract. Track marketing parameters and score leads dynamically.',
              businessValue:
                'Reduce time spent on pipeline management from 2 hours per day to 15 minutes. Automated nurturing can increase conversion rates by 20-30% compared to manual systems.',
              features: [
                'Visual Kanban board with drag-and-drop',
                'Automated lead attribution from first click',
                'Smart lead scoring to prioritize hot leads',
                '40+ reason codes for lead lifecycle tracking',
              ],
              color: '#38BDF8',
            },
            {
              icon: FileText,
              title: 'Invoicing & Financial Management',
              description:
                'Generate professional invoices with line items, tax calculations, and payment tracking. Accept payments directly through invoices. Track revenue, costs, and profit margins in real-time.',
              businessValue:
                'Get paid faster with professional invoices. Automated payment reminders reduce payment delays by 20-30% and overdue invoices by 40-50%. Complete financial visibility maps profitable vs unprofitable jobs.',
              features: [
                'Professional invoice generation with branding',
                '200+ pre-loaded catalog items',
                'Payment processing integration (Stripe, QuickBooks)',
                'Real-time financial analytics dashboard',
              ],
              color: '#10B981',
            },
            {
              icon: Calendar,
              title: 'Smart Scheduling & Territory Management',
              description:
                'Sync appointments with Google Calendar. Automatically assign leads to sales reps based on territory boundaries. Optimize routes to reduce travel time and expand geographic efficiency.',
              businessValue:
                'Reduce lead assignment time from 15 minutes per lead to zero. Increase sales rep calendar utilization by 20-30% through automated scheduling. Eliminate territorial conflicts completely.',
              features: [
                'Google Calendar sync and route optimization',
                'Territory-based lead assignment',
                'Live scheduling with real-time availability',
                'Storm targeting and automated canvassing',
              ],
              color: '#6366F1',
            },
            {
              icon: CheckSquare,
              title: 'Project & Production Management',
              description:
                'Track every job from lead to completion with a 6-stage pipeline. Order materials, assign crews, and track job progress. Upload photos and documents directly to the job file for a complete audit trail.',
              businessValue:
                'Never lose track of a project. Reduce project delays by 30% and improve customer communication. See which projects are profitable and identify operational bottlenecks.',
              features: [
                '6-stage project pipeline (Lead → Estimate → Proposal → Contract → Production → Complete)',
                'Material ordering and inventory tracking',
                'Project photo repository with customer portal access',
                'Complete project documentation and audit trail',
              ],
              color: '#3B82F6',
            },
          ].map((item, index) => (
            <div
              key={index}
              className="glass-panel rounded-3xl p-8 md:p-10 tech-grid-bg reveal-on-scroll border border-slate-800"
            >
              <div className="flex flex-col md:flex-row gap-8">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: `${item.color}18`,
                    border: `1px solid ${item.color}40`,
                  }}
                >
                  <item.icon className="w-8 h-8 text-white" style={{ color: item.color }} />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-4">{item.title}</h3>
                  <p className="body-lg text-white/70 leading-relaxed mb-6">{item.description}</p>

                  <div className="rounded-2xl p-5 mb-6 bg-slate-900/60 border border-slate-800">
                    <p className="text-emerald-400 text-sm font-semibold leading-relaxed">
                      <span className="text-white font-bold block mb-1">
                        Business Value Impact:
                      </span>
                      {item.businessValue}
                    </p>
                  </div>

                  <ul className="grid sm:grid-cols-2 gap-3 text-white/80">
                    {item.features.map((feat) => (
                      <li key={feat} className="flex items-start gap-2.5">
                        <Check className="size-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-white/85 leading-relaxed">{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <SectionDivider className="max-w-5xl mx-auto px-4" />

      {/* ── Workflow Example ──────────────────────────────────────── */}
      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="glass-panel rounded-3xl p-8 md:p-12 tech-grid-bg reveal-on-scroll">
            <h2 className="section-h2 font-bold text-white mb-8 text-center">
              Complete Workflow Integration
            </h2>
            <div className="space-y-6 text-white/80">
              {[
                {
                  step: 1,
                  title: 'Lead Capture',
                  desc: 'Homeowner clicks Google Ad → URL tracking captures source → WordPress plugin captures form submission → Lead automatically created with full attribution.',
                },
                {
                  step: 2,
                  title: 'Automated Nurturing',
                  desc: 'Lead automatically assigned to sales rep based on territory → Email/SMS nurturing sequence begins → Automated follow-ups ensure no lead is forgotten.',
                },
                {
                  step: 3,
                  title: 'Estimate & Proposal',
                  desc: 'Satellite measurement generates instant estimate → Multi-tier pricing presented → Professional proposal sent → Digital signature workflow.',
                },
                {
                  step: 4,
                  title: 'Project Management',
                  desc: 'Contract signed → Job automatically created → Materials ordered → Crew assigned → Progress tracked with photos and documentation.',
                },
                {
                  step: 5,
                  title: 'Get Paid',
                  desc: 'Invoice generated automatically → Payment link sent → Automated reminders for overdue invoices → Complete financial visibility.',
                },
              ].map((s) => (
                <div key={s.step} className="flex items-start gap-5">
                  <div className="w-9 h-9 rounded-full border border-slate-700 bg-slate-800 text-white font-mono font-bold flex items-center justify-center flex-shrink-0 text-sm shadow">
                    {s.step}
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-base mb-1">{s.title}</h4>
                    <p className="body-lg text-white/60 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <SectionDivider className="max-w-5xl mx-auto px-4" />

      {/* ── ROI Metrics ───────────────────────────────────────────── */}
      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6">
          {[
            {
              value: 250,
              suffix: '%',
              label: 'Average Marketing ROI',
              description:
                'Complete attribution enables data-driven budget allocation to high-performing campaigns.',
            },
            {
              value: 25,
              suffix: '%',
              label: 'Conversion Rate Increase',
              description:
                'Automated lead nurturing and systematic follow-ups vs typical manual tracking.',
            },
            {
              value: 45,
              suffix: '%',
              label: 'Fewer Overdue Invoices',
              description:
                'Automated payment reminders and instant billing links remove payment bottlenecks.',
            },
          ].map((metric, index) => (
            <div
              key={index}
              className="glass-panel rounded-3xl p-8 text-center reveal-on-scroll border border-slate-800 bg-slate-900/40"
            >
              <div className="text-4xl font-bold text-white font-mono tracking-tight mb-2">
                <AnimatedCounter end={metric.value} suffix={metric.suffix} />
              </div>
              <div className="text-lg font-semibold text-white mb-2">{metric.label}</div>
              <p className="text-white/60 text-sm leading-relaxed">{metric.description}</p>
            </div>
          ))}
        </div>
      </section>

      <SectionDivider className="max-w-5xl mx-auto px-4" />

      {/* ── Product Screenshot ────────────────────────────────────── */}
      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden glass-panel-elevated border border-slate-800">
            <div className="absolute inset-0 rounded-3xl border border-white/[0.06] z-20 pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent z-10" />
            <img
              src="/product-frames/estimates.webp"
              alt="Rooflyzer estimates workspace showing job pricing and estimate status"
              className="w-full h-auto"
              loading="lazy"
            />
            <div className="absolute bottom-6 left-6 z-20 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              <span className="px-3 py-1 rounded-full text-xs font-mono bg-slate-900/90 border border-slate-700 text-slate-200">
                Estimates & Job Pricing
              </span>
            </div>
          </div>
        </div>
      </section>

      <SectionDivider className="max-w-5xl mx-auto px-4" />

      {/* ── CTA ───────────────────────────────────────────────────── */}
      <section className="relative py-24 px-4 text-center overflow-hidden">
        <div className="relative z-10 max-w-3xl mx-auto">
          <div className="glass-panel-elevated rounded-3xl p-10 tech-grid-bg border border-slate-800">
            <TrendingUp className="size-10 text-emerald-400 mx-auto mb-4" />
            <h2 className="section-h2 font-bold text-white mb-4">
              Ready to Transform Your Roofing Business?
            </h2>
            <p className="body-lg text-white/55 mb-8">
              Stop juggling spreadsheets and disconnected single-purpose tools. Get complete
              visibility, automate your operational workflows, and run your entire business from one
              screen.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                size="lg"
                onClick={() => window.location.assign('/demo')}
                className="bg-white text-slate-950 hover:bg-slate-100 font-semibold px-12 shadow-sm"
              >
                See It In Action
                <ArrowRight className="ml-2 size-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => (window.location.href = '/contact')}
                className="border border-slate-700 text-slate-200 hover:text-white hover:bg-slate-800/60 px-8"
              >
                Schedule Demo Call
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContractorsPage;
